import { useTranslations } from '@/hooks/useTranslations';
import { getCachedImageUrl } from '@/lib/image-cache';
import { cn } from '@/lib/utils';
import { MapPin } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface AddressMapThumbnailProps {
    address: string;
    lat?: number | null;
    lng?: number | null;
    /** Tailwind height class for the image. */
    heightClassName?: string;
    className?: string;
}

/**
 * A compact, chrome-free map preview meant to sit inside an existing card —
 * unlike AddressMapCard, which brings its own Card, header and footer and is
 * sized for a full-width panel.
 *
 * Renders a Google static map with a blue pin and links through to Google Maps.
 */
export function AddressMapThumbnail({ address, lat, lng, heightClassName = 'h-32', className }: AddressMapThumbnailProps) {
    const { t } = useTranslations();
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const blobUrlRef = useRef<string | null>(null);
    const loadedKeyRef = useRef<string | null>(null);

    const mapData = useMemo(() => {
        const env = import.meta.env as unknown as Record<string, string | undefined>;
        const apiKey = env.VITE_GOOGLE_MAPS_API_KEY || env.GOOGLE_MAPS_API_KEY;

        if (!apiKey) {
            return null;
        }

        const hasCoordinates = Boolean(lat && lng);
        const trimmedAddress = address?.trim() ?? '';

        if (!hasCoordinates && !trimmedAddress) {
            return null;
        }

        // scale=2 keeps the pin and street labels crisp on retina displays.
        const base = 'https://maps.googleapis.com/maps/api/staticmap?zoom=15&size=400x170&scale=2&maptype=roadmap';

        if (hasCoordinates) {
            const point = `${lat},${lng}`;

            return {
                key: `latlng:${point}`,
                staticMapUrl: `${base}&center=${point}&markers=color:blue|${point}&key=${apiKey}`,
                googleMapsLink: `https://www.google.com/maps?q=${point}`,
            };
        }

        const encoded = encodeURIComponent(trimmedAddress);

        return {
            key: `addr:${trimmedAddress}`,
            staticMapUrl: `${base}&center=${encoded}&markers=color:blue|${encoded}&key=${apiKey}`,
            googleMapsLink: `https://www.google.com/maps/search/?api=1&query=${encoded}`,
        };
    }, [address, lat, lng]);

    useEffect(() => {
        if (!mapData) {
            setImageUrl(null);
            loadedKeyRef.current = null;

            return;
        }

        // The static map for a given point never changes; only refetch when it moves.
        if (loadedKeyRef.current === mapData.key) {
            return;
        }

        let isMounted = true;

        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }

        getCachedImageUrl(mapData.staticMapUrl)
            .then((url) => {
                if (!isMounted) {
                    return;
                }

                blobUrlRef.current = url.startsWith('blob:') ? url : null;
                setImageUrl(url);
                loadedKeyRef.current = mapData.key;
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setImageUrl(mapData.staticMapUrl);
                loadedKeyRef.current = mapData.key;
            });

        return () => {
            isMounted = false;

            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [mapData]);

    if (!mapData) {
        return (
            <div
                className={cn(
                    'bg-muted text-muted-foreground flex flex-col items-center justify-center gap-1.5 rounded-lg border',
                    heightClassName,
                    className,
                )}
            >
                <MapPin className="h-5 w-5 opacity-50" />
                <p className="text-xs">{t('common.map.no_location', 'No location data available')}</p>
            </div>
        );
    }

    return (
        <a
            href={mapData.googleMapsLink}
            target="_blank"
            rel="noopener noreferrer"
            title={t('common.map.open_in_maps', 'Open in Maps')}
            className={cn(
                'focus-visible:ring-ring bg-muted block overflow-hidden rounded-lg border transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:outline-none',
                heightClassName,
                className,
            )}
        >
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={t('common.map.alt', 'Location map')}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    onError={() => setImageUrl(mapData.staticMapUrl)}
                />
            ) : (
                <span className="sr-only">{t('common.map.alt', 'Location map')}</span>
            )}
        </a>
    );
}
